import { useState, useEffect, useRef } from "react";
import { Scan, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import type { Incident as IncidentType } from "@/frontend/lib/types";
import { incidentClassLabels } from "@/frontend/lib/incident-labels";

interface IncidentProps {
  selectedChatId?: string;
  selectedIncidentId?: number;
  onScrollToDate?: (date: string) => void;
}

interface IncidentCardProps {
  incident: IncidentType;
  isSelected: boolean;
  onSelect: () => void;
  onResolve: () => void;
  onDiscard: () => void;
  onReopen: () => void;
  onScrollToDate: (date: string) => void;
}

function IncidentCard({
  incident,
  isSelected,
  onSelect,
  onResolve,
  onDiscard,
  onReopen,
  onScrollToDate,
}: IncidentCardProps) {
  const getBackgroundColor = () => {
    switch (incident.status) {
      case "open":
        return "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 hover:bg-red-100 dark:hover:bg-red-950/40";
      case "resolved":
        return "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900 hover:bg-green-100 dark:hover:bg-green-950/40";
      case "discarded":
        return "bg-gray-50 dark:bg-gray-800/30 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800/40";
      default:
        return "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 hover:bg-red-100 dark:hover:bg-red-950/40";
    }
  };

  const isPersisted = incident.id !== undefined;
  const isOpen = incident.status === "open";
  const isResolved = incident.status === "resolved";
  const isDiscarded = incident.status === "discarded";
  const showReopenButton = (isResolved || isDiscarded) && isPersisted;

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button')) {
      return;
    }

    onScrollToDate(incident.date);

    if (isOpen) {
      onSelect();
    }
  };

  const handleButtonClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  return (
    <div
      className={`incident-card border rounded-lg p-4 cursor-pointer transition-colors ${getBackgroundColor()}`}
      onClick={handleCardClick}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2">
          {isResolved && <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-500 flex-shrink-0" />}
          {isDiscarded && <CheckCircle2 className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />}
          <span className={`text-sm font-semibold text-gray-700 dark:text-gray-300 ${isDiscarded ? 'line-through' : ''}`}>
            {incidentClassLabels[incident.class]}
          </span>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">{incident.date}</span>
      </div>

      <blockquote className={`border-l-4 border-gray-300 dark:border-gray-600 pl-3 py-1 mb-3 text-sm text-gray-700 dark:text-gray-300 italic whitespace-pre-wrap ${isDiscarded ? 'line-through' : ''}`}>
        {incident.excerpt}
      </blockquote>

      {isSelected && isOpen && (
        <div className="flex gap-2 mt-3">
          <Button
            size="sm"
            variant="default"
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={(e) => handleButtonClick(e, onResolve)}
          >
            Resolve
          </Button>
          <Button
            size="sm"
            variant="default"
            className="bg-gray-600 hover:bg-gray-700 text-white"
            onClick={(e) => handleButtonClick(e, onDiscard)}
          >
            Discard
          </Button>
        </div>
      )}

      {showReopenButton && (
        <div className="flex items-center justify-between mt-3">
          <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
            {isResolved ? 'Resolved' : 'Discarded'}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="border-gray-600 text-gray-900 dark:text-gray-100 hover:bg-gray-900 dark:hover:bg-gray-100 hover:text-white dark:hover:text-gray-900 hover:border-gray-900 dark:hover:border-gray-100"
            onClick={(e) => {
              e.stopPropagation();
              onReopen();
            }}
          >
            Reopen
          </Button>
        </div>
      )}
    </div>
  );
}

export function Incident({ selectedChatId, selectedIncidentId: selectedIncidentIdProp, onScrollToDate }: IncidentProps) {
  const [incidents, setIncidents] = useState<IncidentType[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const incidentRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);

  const getIncidentKey = (incident: IncidentType) => {
    return incident.id ? `${incident.id}` : `${incident.date}-${incident.class}`;
  };

  const sortIncidentsByDate = (incidents: IncidentType[]) => {
    return [...incidents].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA;
    });
  };

  useEffect(() => {
    setIncidents([]);
    setSelectedIncidentId(null);
    setIsScanning(false);

    if (selectedChatId) {
      loadIncidents();
    }
  }, [selectedChatId]);

  useEffect(() => {
    if (selectedIncidentIdProp && incidents.length > 0) {
      const incident = incidents.find(inc => inc.id === selectedIncidentIdProp);
      if (incident) {
        const incidentKey = getIncidentKey(incident);
        setSelectedIncidentId(incidentKey);

        setTimeout(() => {
          const incidentElement = incidentRefs.current.get(incidentKey);
          if (incidentElement && containerRef.current) {
            incidentElement.scrollIntoView({ behavior: "smooth", block: "center" });

            const cardElement = incidentElement.querySelector('.incident-card');
            if (cardElement) {
              cardElement.classList.add('incident-highlight');
              setTimeout(() => cardElement.classList.remove('incident-highlight'), 1000);
            }
          }
        }, 100);
      }
    }
  }, [selectedIncidentIdProp, incidents]);

  const loadIncidents = async () => {
    if (!selectedChatId) return;

    setIsLoading(true);
    try {
      const response = await fetch(`/api/chats/${selectedChatId}/incidents`);
      if (response.ok) {
        const data = await response.json();
        setIncidents(sortIncidentsByDate(data.incidents));
      }
    } catch (error) {
      console.error("Error loading incidents:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleScan = async () => {
    if (!selectedChatId) return;

    setIsScanning(true);
    try {
      const response = await fetch(`/api/chats/${selectedChatId}/incidents/scan`, {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        const newIncidents = data.incidents.map((inc: IncidentType) => ({
          ...inc,
          status: "open",
          chatId: selectedChatId,
        }));
        setIncidents(sortIncidentsByDate([...incidents, ...newIncidents]));
      }
    } catch (error) {
      console.error("Error scanning incidents:", error);
    } finally {
      setIsScanning(false);
    }
  };

  const handleResolve = async (incident: IncidentType) => {
    if (!incident.id) {
      const createdIncident = await createIncident(incident, "resolved");
      setIncidents((prev) =>
        sortIncidentsByDate(
          prev.map((inc) =>
            getIncidentKey(inc) === getIncidentKey(incident)
              ? { ...inc, id: createdIncident.id, status: "resolved" as const }
              : inc
          )
        )
      );
    } else {
      await fetch(`/api/incidents/${incident.id}/resolve`, { method: "POST" });
      setIncidents((prev) =>
        sortIncidentsByDate(
          prev.map((inc) => (getIncidentKey(inc) === getIncidentKey(incident) ? { ...inc, status: "resolved" as const } : inc))
        )
      );
    }

    setSelectedIncidentId(null);
  };

  const handleDiscard = async (incident: IncidentType) => {
    if (!incident.id) {
      const createdIncident = await createIncident(incident, "discarded");
      setIncidents((prev) =>
        sortIncidentsByDate(
          prev.map((inc) =>
            getIncidentKey(inc) === getIncidentKey(incident)
              ? { ...inc, id: createdIncident.id, status: "discarded" as const }
              : inc
          )
        )
      );
    } else {
      await fetch(`/api/incidents/${incident.id}/discard`, { method: "POST" });
      setIncidents((prev) =>
        sortIncidentsByDate(
          prev.map((inc) =>
            getIncidentKey(inc) === getIncidentKey(incident) ? { ...inc, status: "discarded" as const } : inc
          )
        )
      );
    }

    setSelectedIncidentId(null);
  };

  const handleReopen = async (incident: IncidentType) => {
    if (!incident.id) {
      return;
    }

    await fetch(`/api/incidents/${incident.id}/reopen`, { method: "POST" });

    const incidentKey = getIncidentKey(incident);
    setIncidents((prev) =>
      sortIncidentsByDate(
        prev.map((inc) => (getIncidentKey(inc) === incidentKey ? { ...inc, status: "open" as const } : inc))
      )
    );
    setSelectedIncidentId(incidentKey);
  };

  const createIncident = async (incident: IncidentType, status: "resolved" | "discarded") => {
    const response = await fetch("/api/incidents", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        chatId: incident.chatId,
        class: incident.class,
        date: incident.date,
        unixtime: incident.unixtime,
        excerpt: incident.excerpt,
        status,
      }),
    });

    const data = await response.json();
    return data.incident;
  };

  const handleScrollToDate = (date: string) => {
    if (onScrollToDate) {
      onScrollToDate(date);
    }
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('incidents-container') || target.classList.contains('incidents-list')) {
      setSelectedIncidentId(null);
    }
  };

  if (!selectedChatId) {
    return (
      <div className="bg-muted rounded-lg p-6 h-full flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Select a chat to scan for incidents</p>
      </div>
    );
  }

  return (
    <div className="bg-muted rounded-lg h-full overflow-hidden flex flex-col incidents-container" onClick={handleContainerClick}>
      <div className="flex items-center justify-between p-6 pb-4 flex-shrink-0">
        <h2 className="text-lg font-semibold">Incidents</h2>
        <Button
          size="sm"
          onClick={handleScan}
          disabled={isScanning}
          className="flex items-center gap-2"
        >
          {isScanning ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Scanning...
            </>
          ) : (
            <>
              <Scan className="h-4 w-4" />
              Scan
            </>
          )}
        </Button>
      </div>

      <div ref={containerRef} className="flex-1 overflow-y-auto px-6 pb-6 incidents-list">
        <div className="space-y-3">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading incidents...
            </div>
          ) : incidents.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {isScanning ? "Analyzing chat..." : "No incidents found. Click Scan to analyze."}
            </p>
          ) : (
            incidents.map((incident) => {
              const incidentKey = getIncidentKey(incident);
              return (
                <div
                  key={incidentKey}
                  ref={(el) => {
                    if (el) {
                      incidentRefs.current.set(incidentKey, el);
                    } else {
                      incidentRefs.current.delete(incidentKey);
                    }
                  }}
                >
                  <IncidentCard
                    incident={incident}
                    isSelected={selectedIncidentId === incidentKey}
                    onSelect={() => setSelectedIncidentId(incidentKey)}
                    onResolve={() => handleResolve(incident)}
                    onDiscard={() => handleDiscard(incident)}
                    onReopen={() => handleReopen(incident)}
                    onScrollToDate={handleScrollToDate}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
